import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/utils.js';
import { disabledFade, focusRing } from '../lib/focus.js';

/* ===========================================================================
 * FilterChip — the toggle chip used for filter presets and scope selections.
 *
 * This is the fourth home for one string. Screens wanted a chip, `packages/ui`
 * had no primitive for one, so each screen wrote its own — and every copy came
 * out `px-3 py-1.5 text-xs` with NO `focus-visible` treatment at all:
 * keyboard-invisible, and under any touch-target floor. A shared `filterChip`
 * constant in apps/web fixed the treatment but not the location: it stayed a
 * class string in the web app, `ReportScopeBar` kept a byte-identical private
 * `presetChip` beside it, and neither could be reached from the installer or
 * the kiosk. Its own comment said `packages/ui` was where it was headed.
 *
 * SELECTED IS A SOLID FILL, so it takes `text-primary-foreground` — the ink
 * meant for a solid fill. That pairing is only correct *because* the fill is
 * solid; the same ink on a `bg-primary/10` tint is the invisible-text bug this
 * codebase has shipped more than once, measured at ~1.2:1 in dark mode.
 *
 * `aria-pressed` carries the state for anyone who never sees the fill, so the
 * chip is never colour-only. It is set from `selected` rather than left to the
 * caller, because a chip whose state is visual-only is exactly what this
 * component exists to prevent.
 *
 * RADIUS is `rounded-xs` (8px), the CHIP role — not `rounded-control` (10px),
 * which it wore because it is built out of a `<button>`. The element is a
 * button; the object is a chip, and the ramp is classed by what a thing IS on
 * screen. Both the mock and §1 of the rules file put a chip at 8px, so this is
 * the corner that stops a row of filter chips reading two pixels rounder than
 * the status chips in the table underneath them.
 *
 * ---------------------------------------------------------------------------
 * TWO SIZES, BECAUSE THE FIFTH AND SIXTH COPIES WERE FORM CONTROLS.
 *
 * The note above stops at "the fourth home for one string", and it undercounted.
 * A later sweep found five MORE hand-rolled pressed-state toggles — the smart
 * search bar's period presets, the shift form's work-day picker, the schedule's
 * day strip, the check-in panel's site chooser, the lifecycle checklist — and
 * they did not merely repeat this component, they DISAGREED WITH EACH OTHER
 * about the one thing it exists to settle:
 *
 *     period presets, site chooser    on → border-primary bg-primary text-primary-foreground
 *     work days, day strip            on → border-primary bg-primary-tint  text-primary-deep
 *
 * Two answers to "what does selected look like", in one product, on the same
 * shape. Nobody chose that; it is what five authors writing the same toggle
 * from memory produces, which is the argument this file already makes once.
 *
 * The reason they did not reach for this component is that they are 38px
 * controls sitting in forms and panels beside `Input`s, and this was a 28px
 * toolbar chip with no way to say otherwise. So `size` says otherwise. `sm` is
 * the chip this has always been and stays the default, so no existing call site
 * moves a pixel; `md` is the same object at the control step — `min-h-control`,
 * the control radius, body type — for a toggle that stands in a row of fields.
 * What does NOT vary with size is the selected treatment, which is the whole
 * point.
 * ======================================================================== */
export interface FilterChipProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-pressed'> {
  /** Whether this chip's filter is currently applied. */
  selected?: boolean;
  /**
   * `sm` (default) — the 28px toolbar chip. `md` — the same object at the
   * control step, for a toggle standing in a row of form fields. See the note
   * above; the selected treatment is identical either way.
   */
  size?: 'sm' | 'md';
  children: ReactNode;
}

/** Height, corner, inline padding and type step, per size. Nothing else. */
const SIZE = {
  /* `min-h-chip` (28px, the handoff's chip step) rather than a height that
     falls out of the padding. It was `py-1.5`, which made it 31px — three
     pixels off a number the mocks use thirty-five times, and no way to correct
     it anywhere. The padding stays small rather than going to zero so an Arabic
     label that wraps GROWS the chip instead of being clipped by the floor,
     which is the same construction `StatusChip` documents one file over. */
  sm: 'min-h-chip rounded-xs px-3 py-0.5 text-label',
  md: 'min-h-control rounded-control px-3 py-1.5 text-copy',
} as const;

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(
  ({ className, selected = false, size = 'sm', children, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      className={cn(
        'inline-flex items-center justify-center gap-1 border',
        SIZE[size],
        // The same finger floor the rest of the control family carries.
        '[@media(pointer:coarse)]:min-h-touch',
        'transition-[background-color,border-color,color] duration-fast ease-standard',
        focusRing,
        cn('disabled:pointer-events-none', disabledFade),
        selected
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-border bg-surface text-muted-foreground hover:border-primary hover:bg-primary/5 hover:text-foreground',
        className
      )}
      {...props}
    >
      {children}
    </button>
  )
);
FilterChip.displayName = 'FilterChip';
